/**
 * F16.B parent route: `/c/${clientSlug}/*`. Reads the slug from the URL,
 * fetches the matching `tenantBranding` row from Convex via `useQuery`,
 * and wraps descendants in `<TenantBrandingProvider>` + `<TenantChrome>`.
 *
 * Auth: portal routes are PUBLIC at the chrome level. Sign-in lands in
 * F16.C (per-portal MSAL). Until then the chrome shows the demo workspace
 * and the disabled "Sign in with Microsoft" button. This intentionally
 * differs from `/` which gates everything behind Clerk.
 *
 * The default SPA's `__root.tsx` already lets `/c/${slug}` pass through
 * its sign-in gate via the `isPortalPath()` branch added in this slice.
 *
 * See DEMO-PLAN.md §F16.B, §F16.C.
 */

import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  TenantBrandingProvider,
  type TenantBranding,
} from '../../lib/tenant/TenantBrandingContext';
import { MsalInstanceProvider } from '../../lib/tenant/MsalInstanceProvider';
import { TenantChrome } from '../../components/branding/TenantChrome';
import { usePortalSkillsAutoSync } from '../../lib/tenant/usePortalSkillsAutoSync';
import { testIds } from '../../lib/testIds';

/**
 * Phase D — the global `useUserBootstrap` mount in Win95Desktop covers
 * portal bootstrap now. This component remains as a render-null stub so
 * the portal route signature stays stable; future portal-scoped one-shot
 * effects can land here.
 */
function PortalDemoBootstrap({
  branding: _branding,
}: {
  branding: TenantBranding;
}) {
  return null;
}

/**
 * F16.E: once MSAL is ready, kick off a Firm-Skills sync for portals that
 * have a `firmSkillsSiteId` configured. The hook itself is idempotent
 * (sessionStorage guard) and demo-aware.
 */
function PortalSkillsAutoSync() {
  usePortalSkillsAutoSync();
  return null;
}

function PortalLoading() {
  return (
    <div
      data-testid={testIds.portal.loading}
      className="min-h-screen flex items-center justify-center bg-white text-black"
    >
      <p className="text-sm">Loading portal&hellip;</p>
    </div>
  );
}

function PortalNotFound({ slug }: { slug: string }) {
  return (
    <div
      data-testid={testIds.portal.notFound}
      className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white text-black px-6 text-center"
    >
      <h1 className="text-2xl font-semibold">Portal not found</h1>
      <p className="text-sm max-w-md">
        No portal is configured at <code className="font-mono">/c/{slug}</code>.
        Double-check the URL, or visit{' '}
        <a href="/" className="underline">
          Repancy
        </a>{' '}
        to use the default workspace.
      </p>
    </div>
  );
}

function PortalRouteComponent() {
  const { clientSlug } = Route.useParams();
  // `useQuery` returns `undefined` while in flight, `null` on miss, row on hit.
  const row = useQuery(api.tenantBranding.getBySlug, { clientSlug });

  if (row === undefined) {
    console.debug(
      `[PortalRoute] tenantBranding lookup pending for slug="${clientSlug}"`,
    );
    return <PortalLoading />;
  }
  if (row === null) {
    // Loud-log the miss — silent PortalNotFound rendering swallows
    // panel-auto-open / SharePoint deep-link / share-link flows
    // because PanelAutoOpener etc. live inside the desktop subtree
    // that never mounts. Tell the operator exactly what's wrong.
    console.error(
      `[PortalRoute] no tenantBranding row for clientSlug="${clientSlug}". ` +
        `Possible causes: typo in URL, portal was deleted, or provisioning ` +
        `failed before tenantBranding was inserted. Check Convex data via ` +
        `npx convex data tenantBranding --limit 100.`,
    );
    return <PortalNotFound slug={clientSlug} />;
  }

  // Convex returns `_id`/`_creationTime` extras. We pass only the
  // schema-typed fields into the provider so consumers can't reach into
  // backend metadata.
  const branding: TenantBranding = {
    clientSlug: row.clientSlug,
    displayName: row.displayName,
    logoStorageId: row.logoStorageId ?? null,
    primaryColor: row.primaryColor,
    lockedTenantId: row.lockedTenantId,
    lockedSiteIds: row.lockedSiteIds,
    firmSkillsSiteId: row.firmSkillsSiteId ?? null,
    firmSkillsDriveId: row.firmSkillsDriveId ?? null,
    demoSiteName: row.demoSiteName,
    practiceArea: row.practiceArea,
    theme: row.theme,
    showDemo: row.showDemo,
  };

  return (
    <TenantBrandingProvider branding={branding}>
      <MsalInstanceProvider lockedTenantId={branding.lockedTenantId}>
        <PortalDemoBootstrap branding={branding} />
        <PortalSkillsAutoSync />
        <TenantChrome>
          <Outlet />
        </TenantChrome>
      </MsalInstanceProvider>
    </TenantBrandingProvider>
  );
}

export const Route = createFileRoute('/c/$clientSlug')({
  component: PortalRouteComponent,
});
